import { FieldLines } from './Field';
import { BettingDisclaimer } from './Disclaimer';
import { fmtOdds, fmtSigned, type BettingRecommendation, type MarketPlay } from '../lib/api';

function playLabel(p: MarketPlay, home: string, away: string): string {
  const team = p.side === 'home' ? home : p.side === 'away' ? away : p.side.toUpperCase();
  if (p.market === 'spread') return `${team} ${fmtSigned(p.line ?? 0, 1)}`;
  if (p.market === 'total') return `${p.side.toUpperCase()} ${p.line}`;
  return `${team} ML`;
}

export function BettingCard({ rec, home, away, compact = false }: { rec: BettingRecommendation; home: string; away: string; compact?: boolean }) {
  const best = rec.best_play;
  const closed = rec.market_status !== 'open';
  return (
    <article className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
        <span className="kicker">Betting coach</span>
        {best && !closed && <span className="pill bg-greenTint text-green">Edge found</span>}
        {!best && !closed && <span className="pill bg-wash text-muted">No edge</span>}
      </div>
      {closed ? (
        <p className="body-muted px-5 pb-5">{rec.market.note ?? 'No open market.'}</p>
      ) : best ? (
        <div className="relative overflow-hidden bg-blue px-4 py-5 text-paper">
          <FieldLines numbers={false} />
          <p className="display relative text-[28px] leading-[0.95] md:text-[38px]">{playLabel(best, home, away)}</p>
          <p className="meta relative mt-1 !text-paper/85">{best.market} {fmtOdds(best.odds)}</p>
          <dl className="relative mt-4 grid grid-cols-3 gap-3">
            <Stat label="Edge" value={fmtSigned(best.edge_percent, 1, '%')} />
            <Stat label="EV / $100" value={fmtSigned(best.expected_value_per_100, 2)} />
            <Stat label="Half Kelly" value={`${(best.kelly.fractional_kelly * 100).toFixed(1)}%`} />
          </dl>
        </div>
      ) : (
        <p className="body-muted px-5 pb-5">The market is priced about right. Nothing worth a bet.</p>
      )}
      {!compact && (rec.key_pattern || rec.contradicting_pattern) && (
        <div className="flex flex-col gap-3 px-5 py-4 text-[14px]">
          {rec.key_pattern && <p><span className="kicker !text-green">For</span> <span className="text-muted">{rec.key_pattern}</span></p>}
          {rec.contradicting_pattern && <p><span className="kicker !text-red">Against</span> <span className="text-muted">{rec.contradicting_pattern}</span></p>}
        </div>
      )}
      <BettingDisclaimer />
    </article>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-t border-white/30 pt-2">
      <dt className="meta !text-[11px] !text-paper/75">{label}</dt>
      <dd className="score mt-0.5 text-[22px] leading-none">{value}</dd>
    </div>
  );
}

export function MarketTable({ rec, home, away }: { rec: BettingRecommendation; home: string; away: string }) {
  const H = ['Play', 'Price', 'Model', 'Market', 'Edge', 'EV / $100', 'Half Kelly'];
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-[14px]">
        <thead>
          <tr className="border-b-2 border-ink bg-wash">{H.map((h, i) => <th key={h} className={`meta whitespace-nowrap px-4 py-2.5 ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rec.all_markets.map((p) => (
            <tr key={`${p.market}-${p.side}`} className="border-t border-line">
              <td className="whitespace-nowrap px-4 py-3 font-medium">{playLabel(p, home, away)}</td>
              <td className="num px-4 py-3 text-right">{fmtOdds(p.odds)}</td>
              <td className="num px-4 py-3 text-right">{(p.model_probability * 100).toFixed(0)}%</td>
              <td className="num px-4 py-3 text-right text-muted">{(p.market_fair_probability * 100).toFixed(0)}%</td>
              <td className={`num px-4 py-3 text-right ${p.edge_percent > 0 ? 'text-green' : 'text-muted'}`}>{fmtSigned(p.edge_percent, 1, '%')}</td>
              <td className={`num px-4 py-3 text-right ${p.positive_ev ? 'text-green' : 'text-muted'}`}>{fmtSigned(p.expected_value_per_100, 2)}</td>
              <td className="num px-4 py-3 text-right">{p.kelly.has_edge ? `${(p.kelly.fractional_kelly * 100).toFixed(1)}%` : '0%'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
